import type { CAAction, CAFormOption } from '@ds/interfaces'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type {
	CAMember,
	ErrorType,
	Pagination,
} from '@/javascript/interfaces'

import {
	DEFAULT_SELECTED_CURRICULUM_NAME,
	PER_PAGE,
	STATUS_CODE,
} from '../helpers/_constants'
import { putMoveToGroup } from '../services/administratorDashboard'
import api from '../services/api'
import type { ComplianceBundle, CurriculumTrack } from '../services/courses/interfaces'
import {
	type ApiTrackCreateEditResponse,
	postTrackCreate,
	postTrackDuplicate,
} from '../services/curriculums'
import type { Member, MemberContact } from '../services/members/interfaces'
import {
	deleteTrainingGroup,
	getAgencyTeam,
	getTeamMember,
	getTeamMembers,
	postAddTrainingGroup,
	putUpdateTrainingGroup,
} from '../services/team'
import type {
	AgencyGroup,
	AgencyTeam,
	DueDateSettings,
	GroupMember,
	NewCurriculum,
	TrainingOption,
} from '../services/team/interfaces'
import { useMembersStore } from '../stores/MembersStore'
import { getAgencyId } from '../utils/getAgencyId'
import { replaceText } from '../utils/stringUtils'
import { useAdministratorStore } from './AdministratorStore'
import { useCurriculumsStore } from './CurriculumsStore'
import { useGlobalStore } from './GlobalStore'
import { useNotificationStore } from './NotificationStore'

export const useTeamStore = defineStore('team', () => {
	// State
	const agencyTeam = ref<AgencyTeam>({} as AgencyTeam)
	const agencyTeamMembers = ref<Member[]>([])
	const complianceBundles = ref<ComplianceBundle[]>([])
	const complianceDueDate = ref('')
	const copiedCurriculumId = ref('')
	const daysBeforeDueDate = ref(10) // eslint-disable-line no-magic-numbers
	const defaultCurriculum = ref<CurriculumTrack | null>(null)
	const defaultGroup = ref<AgencyGroup | null>(null)
	const defaultNewCurriculum = ref<NewCurriculum>({
		curriculum: {
			id: '0',
			name: DEFAULT_SELECTED_CURRICULUM_NAME,
		},
		name: '',
		type: 'duplicate' as TrainingOption,
	})
	const dueDateSettings = ref<DueDateSettings>({} as DueDateSettings)
	const globalStore = useGlobalStore()
	const isDisabledAddTeamMember = ref(false)
	const isDisabledUpdateTrainingGroup = ref(false)
	const isLoadingCurriculums = ref(false)
	const isLoadingDueDateSettings = ref(false)
	const isLoadingGroups = ref(false)
	const isLoadingMembers = ref(false)
	const isProcessing = ref(false)
	const membersPaginationTotal = ref(0)
	const moveToGroupId = ref<number | null>(null)
	const newCurriculum = ref<NewCurriculum>({
		curriculum: {
			id: '',
			name: DEFAULT_SELECTED_CURRICULUM_NAME,
		},
		name: '',
		type: 'blank' as TrainingOption,
	})
	const newGroupName = ref('')
	const notificationStore = useNotificationStore()
	const pagination = ref<Pagination>({
		currentPage: PER_PAGE.CURRENT,
		perPage: PER_PAGE.MEMBERS,
	})
	const radioGroupDueDateIsSingle = ref(false)
	const radioRelativeDueDate = ref(false)
	const radioSingleDueDate = ref(false)
	const selectedCurriculumId = ref<number | null>(null)
	const selectedGroupId = ref<number | null>(null)
	const selectedGroupMembers = ref<GroupMember[]>([])
	const selectedMember = ref<Member | undefined>(undefined)
	const selectedMemberId = ref<number | null>(null)
	const selectedMemberIds = ref<string[]>([])
	const showCreateNewCurriculumSlideover = ref(false)
	const showDeleteCurriculumTrackModal = ref(false)
	const showSelectCurriculumSlideover = ref(false)
	const slideoverCurriculums = ref<ComplianceBundle[] | CurriculumTrack[]>([])
	const slideoverRadioButtonIdPrefix = ref('curriculum-select-')
	const trainingCurriculums = ref<CurriculumTrack[]>([])
	const trainingGroups = ref<AgencyGroup[]>([])

	// Getters
	const activeAgencyTeamMembers = computed(() => {
		return agencyTeamMembers.value.filter(member => member.status === 'active')
	})

	const activeSeatMembers = computed(() => {
		return agencyTeamMembers.value.filter(member => !member.admin_preview)
	})

	const checkedInitialTraining = computed(() => {
		return (
			radioSingleDueDate.value && !radioRelativeDueDate.value ||
			radioGroupDueDateIsSingle.value
		)
	})

	const curriculumsStore = computed(() => {
		return useCurriculumsStore()
	})

	const curriculumTextComplianceBundle = computed(() => {
		return `Select one of CareAcademy's pre-built compliance curricula.
		You can customize this curriculum's training at any time after creating the training group.`
	})

	const curriculumTextDuplicate = computed(() => {
		return (
			<>
				Select one of your existing curricula or create a new one. Team members in{' '}
				<span class="text-blue-400">
					{!selectedGroup.value ? '[training group name]' : selectedGroup.value?.name}
				</span>{' '}
				will be automatically assigned to this curriculum.
			</>
		)
	})

	const curriculumTextNoMatch = computed(() => {
		return 'There are no curricula to match your search.'
	})

	const defaultCurriculumComputed = computed({
		get: (): CurriculumTrack | null => {
			return (
				defaultCurriculum.value ??
				trainingCurriculums.value.find(curriculum => curriculum.is_default) ??
				null
			)
		},
		set: (value: CurriculumTrack | null) => {
			defaultCurriculum.value = value
		},
	})

	const defaultGroupComputed = computed({
		get: (): AgencyGroup | null => {
			return (
				defaultGroup.value ??
				trainingGroups.value.find(group => group.is_default) ??
				null
			)
		},
		set: (value: AgencyGroup | null) => {
			defaultGroup.value = value
		},
	})

	const isChangingCurriculum = computed(() => {
		return globalStore.emittedEvent === 'change-group-curriculum'
	})

	const listOfTrainingGroupIds = computed(() => {
		return trainingGroups.value.map(group => group.id)
	})

	const membersToMessage = computed((): MemberContact[] => {
		switch (globalStore.emittedEvent) {
			case 'send-bulk-message':
				return globalStore.selectedCATableMembers.map((member: CAMember) => ({
					id: Number(member.id),
					name: member.name,
					phone_number: member.phone,
				}))

			case 'send-group-message':
				return selectedGroupMembers.value.map(member => ({
					id: member.id,
					name: member.name,
					phone_number: member.phone,
				}))

			case 'send-member-message':
				return [
					{
						id: selectedMember.value?.id ?? 0,
						name: `${selectedMember.value?.first_name} ${selectedMember.value?.last_name}`,
						phone_number: selectedMember.value?.phone_number,
					},
				]

			default:
				return []
		}
	})

	const selectedCurriculum = computed((): CurriculumTrack | null => {
		return (
			trainingCurriculums.value.find(
				curriculum => curriculum.id === selectedCurriculumId.value
			) ?? null
		)
	})

	const moveToGroup = computed((): AgencyGroup | undefined => {
		return trainingGroups.value.find(group => group.id === moveToGroupId.value)
	})

	const selectedGroup = computed((): AgencyGroup | undefined => {
		return trainingGroups.value.find(group => group.id === selectedGroupId.value)
	})

	const slideoverComplianceBundles = computed((): CAFormOption[] => {
		if (!complianceBundles.value) {
			return []
		}

		return complianceBundles.value.map((curriculum: ComplianceBundle | CurriculumTrack) => ({
			id: `${slideoverRadioButtonId.value}${curriculum.id}`,
			label: `${curriculum.name}`,
			tags: !!getGroupsUsingTrainingCurriculum(curriculum.id).length
				? getGroupsUsingTrainingCurriculum(curriculum.id).map(
					(group: AgencyGroup) => group.name
				)
				: [],
			tagsLabel: 'used by:',
		}))
	})

	const slideoverCurriculumId = computed(() => {
		const id = `${replaceText(
			slideoverRadioButtonId.value,
			'',
			newCurriculum.value.curriculum.id
		)}`

		return (
			id ??
			`${replaceText(slideoverRadioButtonId.value, '', `${selectedCurriculumId.value}`)}`
		)
	})

	const slideoverRadioButtonId = computed(() => {
		// NOTE: `curriculum-change-` or `curriculum-select-` is added to the id to avoid conflicts with the radio group
		return isChangingCurriculum.value && !showSelectCurriculumSlideover.value
			? 'curriculum-change-'
			: 'curriculum-select-'
	})

	const slideoverTrainingCurriculums = computed((): CAFormOption[] => {
		if (!slideoverCurriculums.value) {
			return []
		}

		return slideoverCurriculums.value.map((curriculum: ComplianceBundle | CurriculumTrack) => ({
			actions:
				isChangingCurriculum.value && !showSelectCurriculumSlideover.value
					? ([
						{
							color: 'danger',
							dataTest: 'action-delete-curriculum',
							icon: 'SVGTrash',
							id: `delete-curriculum-${curriculum.id}`,
							name: 'delete',
							tooltip: `Delete ${curriculum.name}`,
						},
					] as CAAction[])
					: undefined,
			id: `${slideoverRadioButtonId.value}${curriculum.id}`,
			label: `${curriculum.name}`,
			tags: !!getGroupsUsingTrainingCurriculum(curriculum.id).length
				? getGroupsUsingTrainingCurriculum(curriculum.id).map(
					(group: AgencyGroup) => group.name
				)
				: [],
			tagsLabel: 'used by:',
		}))
	})

	// Actions
	const agencyDueDateSettings = async () => {
		isLoadingDueDateSettings.value = true

		try {
			const response = await getAgencyTeam(api, getAgencyId())

			dueDateSettings.value = response.agency.due_dates
			globalStore.agencyBackOffice = {
				backOfficeIntegrationEnabled: response.agency.back_office_integration_enabled,
				backOfficeIntegrationProviderName:
					response.agency.back_office_integration_provider_name,
				marketingProductName: response.agency.marketing_product_name,
			}
		} catch (error) {
			notificationStore.onHandleErrorMessages({ error: error as ErrorType })
		} finally {
			isLoadingDueDateSettings.value = false
		}
	}

	const deleteGroup = async () => {
		try {
			await deleteTrainingGroup(api, getAgencyId(), selectedGroupId.value!)
			await getAgencyTeamGroups()

			notificationStore.onHandleSuccessMessages({ message: 'Group deleted successfully.' })

			globalStore.clearSelectedValues()
			globalStore.resetCAModal()
		} catch (error) {
			notificationStore.onHandleErrorMessages({ error: error as ErrorType })
		}
	}

	const getAgencyTeamGroups = async () => {
		isLoadingGroups.value = true

		try {
			const response = await getAgencyTeam(api, getAgencyId())

			agencyTeam.value = response
			dueDateSettings.value = response.agency.due_dates
			complianceBundles.value = response.agency.prebuilt_tracks
			trainingGroups.value = response.agency.training_groups
			trainingCurriculums.value = response.agency.training_programs.map(curriculum => ({
				...curriculum,
				id: curriculum.id,
			}))
			slideoverCurriculums.value = trainingCurriculums.value
			newCurriculum.value.type = 'blank'

			selectedCurriculumId.value =
				trainingCurriculums.value[trainingCurriculums.value.length - 1].id
			newCurriculum.value.curriculum.id = `curriculum-change-${selectedCurriculumId.value}`

			const trainingCurriculum = response.agency.training_programs.find(
				curriculum => curriculum.is_default
			)

			if (trainingCurriculum && !selectedCurriculumId.value) {
				selectedCurriculumId.value = trainingCurriculum.id
			} else {
				selectedCurriculumId.value =
					selectedGroup.value?.training_program.id ?? selectedCurriculumId.value
			}

			curriculumsStore.value.curriculumTracks = response.agency.training_programs

			globalStore.agencyBackOffice = {
				backOfficeIntegrationEnabled: response.agency.back_office_integration_enabled,
				backOfficeIntegrationProviderName:
					response.agency.back_office_integration_provider_name,
				marketingProductName: response.agency.marketing_product_name,
			}
		} catch (error) {
			notificationStore.onHandleErrorMessages({ error: error as ErrorType })
		} finally {
			isLoadingGroups.value = false
		}
	}

	const getAgencyTeamMembersAction = async ({
		groupId = null,
		page = 1,
		perPage = PER_PAGE.MEMBERS,
		query = '',
		roles = [],
		sortBy = 'name',
		sortDirection = 'asc',
		statuses = ['active'],
		trainingGroups = [],
	}: {
		groupId: number | null
		page?: number
		perPage?: number
		query?: string
		roles?: string[]
		sortBy?: string
		sortDirection?: string
		statuses?: string[]
		trainingGroups?: string[]
	}) => {
		isLoadingMembers.value = true

		try {
			const response = await getTeamMembers(api, {
				agency_id: getAgencyId(),
				group_ids: groupId ? [
					`${groupId}`,
					...trainingGroups,
				] : trainingGroups,
				page,
				perPage,
				query,
				roles,
				sort_by: sortBy,
				sort_direction: sortDirection,
				statuses,
			})

			agencyTeamMembers.value = response.data

			if (groupId) {
				selectedGroupMembers.value = response.data.map((member: Member) => ({
					email: member.email_address ?? '',
					id: member.id,
					name: member.name ?? '',
					phone: member.phone_number ?? '',
					role: member.role,
					stringToSearch: member.name ?? '',
				}))
			}

			pagination.value = {
				currentPage: response['pagination']['current_page'] ?? PER_PAGE.CURRENT,
				perPage: response['pagination']['per_page'] ?? PER_PAGE.MEMBERS,
			}

			membersPaginationTotal.value = response['pagination']['total']
		} catch (error) {
			notificationStore.onHandleErrorMessages({ error: error as ErrorType })
		} finally {
			isLoadingMembers.value = false
		}
	}

	const getGroupsUsingTrainingCurriculum = (curriculumId: number): AgencyGroup[] => {
		return trainingGroups.value.filter(
			(group: AgencyGroup) => group.training_program.id === curriculumId
		)
	}

	const getTeamMemberDetails = async ({
		agencyId,
		memberId,
	}: {
		agencyId: number
		memberId: number
	}) => {
		isLoadingMembers.value = true

		try {
			const response = await getTeamMember(api, { agencyId, memberId })

			selectedMemberId.value = memberId
			selectedMember.value = response

			if (selectedMemberId.value && selectedMember.value?.status === 'active') {
				await useMembersStore().fetchMemberDetails(selectedMemberId.value)
			}
		} catch (error) {
			notificationStore.onHandleErrorMessages({ error: error as ErrorType })
		} finally {
			isLoadingMembers.value = false
		}
	}

	const handleMoveToGroupSave = async (memberIds?: number[]) => {
		if (!moveToGroupId.value) {
			notificationStore.onHandleErrorMessages({ error: { message: 'Please select a group to move the members to.' } })

			return
		}

		const selectedMemberIdsToMove =
			memberIds ||
			useAdministratorStore().getSelectedMemberIds() ||
			selectedMemberIds.value

		if (!selectedMemberIdsToMove.length) {
			notificationStore.onHandleErrorMessages({ error: { message: 'Please select at least one member to move.' } })

			return
		}

		try {
			await putMoveToGroup(api, {
				agencyId: getAgencyId(),
				groupId: moveToGroupId.value,
				memberIds: selectedMemberIdsToMove,
			})
		} catch (error: unknown) {
			if (error && typeof error === 'object' && 'status' in error && error.status === STATUS_CODE.NOT_FOUND) {
				// TODO: Axios is redirecting with the 302 response from the API, and we do not want to do that. Error can be added back when that is fixed.
			} else {
				notificationStore.onHandleErrorMessages({ error: error as ErrorType })
			}
		} finally {
			moveToGroupId.value = null
			globalStore.clearSelectedValues()
			globalStore.resetCAModal()
		}
	}

	const mapIdsToMembers = (ids: number[]): Member[] => {
		return agencyTeamMembers.value.map(item => item).filter(member => ids.includes(member.id))
	}

	const onCancelCreateCurriculum = (cancelingCreateNewCurriculum?: boolean) => {
		resetNewCurriculum(cancelingCreateNewCurriculum)
		showCreateNewCurriculumSlideover.value = false

		if (cancelingCreateNewCurriculum) {
			newCurriculum.value.type = 'blank'
		}
	}

	const onCancelSelectCurriculum = () => {
		newCurriculum.value.curriculum = {
			id: '',
			name: DEFAULT_SELECTED_CURRICULUM_NAME,
		}
		toggleSlideover()
	}

	const onSubmitSelectedCurriculum = () => {
		const idString = `${replaceText(
			slideoverRadioButtonIdPrefix.value,
			'',
			newCurriculum.value.curriculum.id
		)}`
		const id = parseInt(idString)

		copiedCurriculumId.value = `${id}`

		newCurriculum.value.curriculum = {
			id: newCurriculum.value.curriculum.id,
			name:
				slideoverCurriculums.value.find(curriculum => curriculum.id === id)?.name ??
				newCurriculum.value.curriculum.name,
		}

		showSelectCurriculumSlideover.value = false
	}

	const onUpdateSelectCurriculum = (id: string, name: string) => {
		newCurriculum.value.curriculum = { id, name }
		selectedCurriculumId.value = parseInt(id)

		if ('created_at' in slideoverCurriculums.value[0]) {
			slideoverCurriculums.value = [
				...slideoverCurriculums.value.filter(curriculum => curriculum.id !== 0),
				{
					courseTotal: 0,
					durationInMinutes: 0,
					id: parseInt(id),
					is_default: false,
					name,
				} as CurriculumTrack,
			] as CurriculumTrack[]
		} else {
			slideoverCurriculums.value = [
				...slideoverCurriculums.value.filter(curriculum => curriculum.id !== 0),
				{
					courseTotal: 0,
					durationInMinutes: 0,
					id: parseInt(id),
					name,
				} as ComplianceBundle,
			] as ComplianceBundle[]
		}
	}

	const openCreateNewCurriculumSlideover = () => {
		resetNewCurriculum(true)
		newCurriculum.value.type = 'duplicate'
		showCreateNewCurriculumSlideover.value = true
	}

	const postAddNewTrainingGroup = async (form?: {
		newGroupName: string
		selectedCurriculumId: number
		selectedMemberIds: string[]
	}) => {
		if (form) {
			const params = {
				agency_id: getAgencyId(),
				caregiver_ids: form.selectedMemberIds,
				name: form.newGroupName,
				source_curriculum_track_id: form.selectedCurriculumId,
			}

			try {
				let curriculumTrackId: number | null = null
				let response: ApiTrackCreateEditResponse | undefined = undefined

				response = await postTrackDuplicate(api, params)

				curriculumTrackId = response?.curriculum_track.id ?? selectedCurriculumId.value

				await postAddTrainingGroup(api, {
					agency_id: params.agency_id,
					caregiver_ids: params.caregiver_ids,
					curriculum_track_id: curriculumTrackId!,
					name: params.name,
				})

				notificationStore.onHandleSuccessMessages({ message: `${params.name} added successfully.` })

				resetNewCurriculum()
			} catch (error) {
				notificationStore.onHandleErrorMessages({ error: error as ErrorType })
			}

			return
		}

		const params = {
			agency_id: getAgencyId(),
			caregiver_ids: selectedMemberIds.value,
			curriculum_track_id: selectedCurriculumId.value!,
			name: newGroupName.value,
			source_curriculum_track_id:
				newCurriculum.value.type === 'pre-built' || newCurriculum.value.type === 'duplicate'
					? copiedCurriculumId.value
					: defaultCurriculumComputed.value?.id ?? selectedCurriculumId.value ?? 0,
		}

		try {
			let curriculumTrackId: number | null = null
			let response: ApiTrackCreateEditResponse | undefined = undefined

			switch (newCurriculum.value.type) {
				case 'duplicate':
				case 'pre-built':
					response = await postTrackDuplicate(api, {
						...params,
						name: newCurriculum.value.name,
					})
					break
				case 'build':
					response = await postTrackCreate(api, {
						...params,
						name: newCurriculum.value.name,
					})
					break
			}

			curriculumTrackId = response?.curriculum_track.id ?? selectedCurriculumId.value

			await postAddTrainingGroup(api, {
				agency_id: params.agency_id,
				caregiver_ids: params.caregiver_ids,
				curriculum_track_id: curriculumTrackId!,
				name: params.name,
			})

			notificationStore.onHandleSuccessMessages({ message: `${params.name} added successfully.` })
		} catch (error) {
			notificationStore.onHandleErrorMessages({ error: error as ErrorType })
		} finally {
			resetNewCurriculum()
		}
	}

	const putUpdateAgencyTrainingGroup = async (group: AgencyGroup) => {
		isProcessing.value = true

		try {
			await putUpdateTrainingGroup(api, {
				agency_id: getAgencyId(),
				curriculum_track_id: group.training_program.id,
				id: group.id,
				is_default: group.is_default,
				name: group.name,
			})
			await getAgencyTeamGroups()

			notificationStore.onHandleSuccessMessages({ message: `${group.name} updated successfully.` })
		} catch (error) {
			notificationStore.onHandleErrorMessages({ error: error as ErrorType })
		} finally {
			isProcessing.value = false
		}
	}

	const resetNewCurriculum = (keepContent?: boolean) => {
		copiedCurriculumId.value = ''

		if (!keepContent) {
			newGroupName.value = ''
			newCurriculum.value.name = ''
			newCurriculum.value.type = 'blank'
			setSlideoverValues()
		}

		newCurriculum.value.curriculum = {
			id: '',
			name: DEFAULT_SELECTED_CURRICULUM_NAME,
		}

		selectedMemberIds.value = []
	}

	const setSlideoverValues = (curriculumId?: string) => {
		if (newCurriculum.value.type === 'pre-built') {
			if (!complianceBundles.value.length) {
				return
			}

			slideoverCurriculums.value = complianceBundles.value
		} else {
			newCurriculum.value.type = 'duplicate'
			slideoverCurriculums.value = trainingCurriculums.value

			if (curriculumId) {
				const id = curriculumId
					? curriculumId
					: String(
						defaultCurriculumComputed.value?.id ??
						defaultNewCurriculum.value.curriculum.id
					)
				const name =
					slideoverTrainingCurriculums.value.find(curriculum => curriculum.id === id)
						?.label ?? newCurriculum.value?.name

				copiedCurriculumId.value = id
				newCurriculum.value.curriculum = {
					id: `${slideoverRadioButtonId.value}${id}`,
					name: `Copy of ${name}`,
				}
			}
		}
	}

	const toggleSlideover = () => {
		showCreateNewCurriculumSlideover.value = true
		showSelectCurriculumSlideover.value = false
	}

	return {
		// State
		activeAgencyTeamMembers,
		activeSeatMembers,
		agencyTeam,
		agencyTeamMembers,
		checkedInitialTraining,
		complianceBundles,
		complianceDueDate,
		copiedCurriculumId,
		curriculumTextComplianceBundle,
		curriculumTextDuplicate,
		curriculumTextNoMatch,
		curriculumsStore,
		daysBeforeDueDate,
		defaultCurriculum: defaultCurriculumComputed,
		defaultGroup: defaultGroupComputed,
		defaultNewCurriculum,
		dueDateSettings,
		isChangingCurriculum,
		isDisabledAddTeamMember,
		isDisabledUpdateTrainingGroup,
		isLoadingCurriculums,
		isLoadingDueDateSettings,
		isLoadingGroups,
		isLoadingMembers,
		isProcessing,
		listOfTrainingGroupIds,
		membersPaginationTotal,
		membersToMessage,
		moveToGroup,
		moveToGroupId,
		newCurriculum,
		newGroupName,
		pagination,
		radioGroupDueDateIsSingle,
		radioRelativeDueDate,
		radioSingleDueDate,
		selectedCurriculum,
		selectedCurriculumId,
		selectedGroup,
		selectedGroupId,
		selectedGroupMembers,
		selectedMember,
		selectedMemberId,
		selectedMemberIds,
		showCreateNewCurriculumSlideover,
		showDeleteCurriculumTrackModal,
		showSelectCurriculumSlideover,
		slideoverComplianceBundles,
		slideoverCurriculumId,
		slideoverCurriculums,
		slideoverRadioButtonId,
		slideoverTrainingCurriculums,
		trainingCurriculums,
		trainingGroups,

		// Actions
		agencyDueDateSettings,
		deleteGroup,
		getAgencyTeamGroups,
		getAgencyTeamMembers: getAgencyTeamMembersAction,
		getGroupsUsingTrainingCurriculum,
		getTeamMemberDetails,
		handleMoveToGroupSave,
		mapIdsToMembers,
		onCancelCreateCurriculum,
		onCancelSelectCurriculum,
		onSubmitSelectedCurriculum,
		onUpdateSelectCurriculum,
		openCreateNewCurriculumSlideover,
		postAddNewTrainingGroup,
		putUpdateAgencyTrainingGroup,
		resetNewCurriculum,
		setSlideoverValues,
		toggleSlideover,
	}
})
